<template>
	<div class="widget-builder order-tracking-builder" ref="builderRef">
		<LoadingOverlay :visible="isLoading" :message="t('Loading')" />

		<v-card class="filters-card" flat>
			<v-card-title>
				<UserViewControl :view-key="'OrderTracking'" :get-view-state="getUserViewState" :apply-view-state="applyUserViewState" :default-view-state="getDefaultUserViewState" :is-ready="isViewReady" :state-version="viewStateVersion">
					<template #actions>
						<v-badge :model-value="activeFilterCount > 0" :content="activeFilterCount" color="primary">
							<v-btn icon="filter_alt" variant="tonal" color="primary" density="comfortable" :disabled="!isViewReady" @click="isFilterSheetOpen = true" />
						</v-badge>
					</template>
				</UserViewControl>

				<FilterChips :chips="selectedFilterChips" @select="onFilterChipClick" @remove="removeFilterChip" @clear="resetFilters" />
			</v-card-title>

			<FilterControls ref="filterControls" :filters="filterDefinitions" :mobile="isLaptop" layout="panel" v-model:open="isFilterSheetOpen" @apply="onFiltersApply" @reset="resetFilters" @update:active-count="activeFilterCount = $event" />
		</v-card>

		<div class="order-tracking-builder__context">
			<v-chip color="primary" variant="tonal" readonly>
				<v-icon start>date_range</v-icon>
				{{ t('AppliedPeriod') }} {{ periodLabel }}
				<span v-if="periodMonths > 0">· {{ periodMonths }} {{ t('Months') }}</span>
			</v-chip>

			<div class="order-tracking-builder__focus">
				<span class="order-tracking-builder__focus-label">{{ t('MetricFocus') }}</span>
				<QuickColumn v-model="focusMetrics" :columns="metricOptions" icon-mode="visibility" :min-selected="1" />
			</div>
		</div>

		<v-alert v-if="showTruncationWarning" type="warning" variant="tonal" density="comfortable" class="mb-4">
			{{ truncationMessage }}
		</v-alert>

		<v-expansion-panels :model-value="expandedBlocks.cumul ? 'cumul' : undefined" @update:model-value="expandedBlocks.cumul = $event === 'cumul'">
			<v-expansion-panel value="cumul" class="widget-builder--panel" eager>
				<v-expansion-panel-title>
					<v-icon>functions</v-icon>
					{{ t('PeriodCumul') }}
				</v-expansion-panel-title>
				<v-expansion-panel-text>
					<div class="order-tracking-builder__rates">
						<v-chip color="success" variant="tonal" readonly>
							<v-icon start>check_circle</v-icon>
							{{ t('ServiceRate') }}
							<strong class="ml-1">{{ formatPercent(totals.serviceRate[primaryMetric]) }}</strong>
						</v-chip>
						<v-chip v-if="showReturns" color="warning" variant="tonal" readonly>
							<v-icon start>local_shipping</v-icon>
							{{ t('ReturnRate') }}
							<strong class="ml-1">{{ formatPercent(totals.returnRate[primaryMetric]) }}</strong>
						</v-chip>
					</div>

					<div class="order-tracking-builder__cumul">
						<MetricCumulCard v-if="showValue" :title="t('Turnover')" icon="paid" :metric="METRIC.VALUE" :totals="totals" :objectives="objectivesForDisplay.value" :show-returns="showReturns" :mobile="isLaptop" />
						<MetricCumulCard v-if="showWeight" :title="t('Weight')" icon="weight" :metric="METRIC.WEIGHT" :totals="totals" :objectives="objectivesForDisplay.weight" :show-returns="showReturns" :mobile="isLaptop" />
					</div>

					<RatioTiles class="mt-4" :ratios="totals.ratios" :show-value="showValue" :show-weight="showWeight" />
				</v-expansion-panel-text>
			</v-expansion-panel>
		</v-expansion-panels>

		<MetricAnalysisBlock v-if="showValue" v-model:expanded="expandedBlocks.value" v-model:level="categoryLevel" :title="t('Turnover')" icon="paid" panel-key="value" :metric="METRIC.VALUE" :monthly-series="monthlySeries" :categories="valueCategories" :level-options="levelOptions" :show-returns="showReturns" :stacked="isLaptop" />

		<MetricAnalysisBlock v-if="showWeight" v-model:expanded="expandedBlocks.weight" v-model:level="categoryLevel" :title="t('Weight')" icon="weight" panel-key="weight" :metric="METRIC.WEIGHT" :monthly-series="monthlySeries" :categories="weightCategories" :level-options="levelOptions" :show-returns="showReturns" :stacked="isLaptop" />

		<DetailGridCard :title="t('DetailByStore')" :count="visibleDetailRows.length" v-model:search="searchText">
			<template #chips>
				<QuickColumn v-if="showReturns" v-model="detailOrderTypes" :columns="orderTypeOptions" :min-selected="1" />
			</template>
			<template #groups>
				<QuickRowGroup v-if="!isLaptop" v-model="selectedRowGroups" :groups="rowGroupDefinitions" />
			</template>

			<div class="kpi-detail-card__grid-shell">
				<div v-show="visibleDetailRows.length > 0" class="kpi-detail-card__grid">
					<AgGrid v-if="!isLaptop" ref="gridRef" :rowData="visibleDetailRows" :colDefs="columnDefs" :getGroupRowAgg="groupRowAgg" groupDisplayType="singleColumn" :groupDefaultExpanded="1" :sideBar="isTouchMode ? false : true" :row-group-panel-show="isTouchMode ? 'false' : 'always'" :pagination="true" :paginationPageSize="50" @row-group-columns-changed="selectedRowGroups = $event" @view-state-changed="bumpViewStateVersion" />

					<TouchCardData v-else :row-data="visibleDetailRows" :columns="mobileColumns" title-field="storeName" :subtitle-fields="['companyName', 'channelName']" row-key="id" metric-layout="rows" :groups="rowGroupDefinitions" v-model:group-columns="selectedRowGroups" :group-fields="rowGroupFieldByColId" :get-group-row-agg="groupRowAgg" :group-leaf-label="t('DeliveryLocations')" leaf-count-field="deliveryLocationId" sort-storage-key="orderTrackingDetail" />
				</div>

				<v-sheet v-if="!isLoading && visibleDetailRows.length === 0" class="kpi-detail-card__empty kpi-detail-card__empty--overlay" rounded>
					<v-icon>table_rows_narrow</v-icon>
					<span>{{ t('NoDeliveryLocation') }}</span>
				</v-sheet>
			</div>
		</DetailGridCard>

		<DialogControl v-model="isVolumeDialogOpen" :max-width="'440px'" :attach="overlayTarget || undefined">
			<v-card>
				<v-card-title>
					<v-icon>warning</v-icon>
					{{ t('Warning') }}
				</v-card-title>
				<v-card-text>{{ volumeTooLargeMessage }}</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn variant="tonal" color="primary" @click="isVolumeDialogOpen = false">{{ t('Ok') }}</v-btn>
				</v-card-actions>
			</v-card>
		</DialogControl>
	</div>
</template>

<script setup>
import { ref, reactive, computed, inject, onMounted, onBeforeUnmount, watch } from 'vue';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { useTranslator } from '@/composables/useTranslator';
import { log } from '@/utils/core/vueLogger.js';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { buildFilterChips, scrollFilterFieldIntoView } from '@/utils/ui/filterChips';
import { buildPresets, ORDER_TRACKING_PRESET_KEYS, presetRange, serializePeriodSelection, resolvePeriodSelection } from '@/utils/domain/periodPresets';
import { formatNumber, formatPercent, formatPeriodLabel } from '@/utils/core/format';
import { getDirectOrders, MAX_OFFLINE_MONTHS, MAX_NETWORK_ORDERS } from '@/api/sales/order';
import { getDeliveryLocations } from '@/api/sales/deliveryLocation';
import { getOrganisations } from '@/api/core/organisation';
import { getAccounts } from '@/api/distributor/account';
import { getChannels } from '@/api/distributor/channel';
import { getProductTrees } from '@/api/core/productTree';
import { getAssortProducts } from '@/api/assort/assort';
import { METRIC, ORDER_TYPE, filterOrders, aggregatePeriod, aggregateByMonth, aggregateByCategory, aggregateByDeliveryLocation, resolveObjectives, periodMonthCount, isPeriodWithinObjective, ownerOrganisationScope } from '@/services/directOrderService';
import { toDetailRow, buildColumnDefs, buildGroupRowAgg, buildMobileColumns } from '@/components/orderTracking/orderTrackingColumns';
import { loadPeriodSelection, savePeriodSelection } from '@/components/orderTracking/orderTrackingSettings';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import UserViewControl from '@/components/controls/UserViewControl.vue';
import FilterControls from '@/components/controls/FilterControls.vue';
import FilterChips from '@/components/controls/FilterChips.vue';
import DialogControl from '@/components/controls/DialogControl.vue';
import DetailGridCard from '@/components/kpi/DetailGridCard.vue';
import AgGrid from '@/components/agGrid/AgGrid.vue';
import TouchCardData from '@/components/agGrid/TouchCardData.vue';
import QuickRowGroup from '@/components/agGrid/QuickRowGroup.vue';
import QuickColumn from '@/components/agGrid/QuickColumn.vue';
import MetricCumulCard from '@/components/orderTracking/MetricCumulCard.vue';
import RatioTiles from '@/components/orderTracking/RatioTiles.vue';
import MetricAnalysisBlock from '@/components/orderTracking/MetricAnalysisBlock.vue';

defineProps({
	title: { type: String, default: '' },
	params: { type: Object, default: () => ({}) },
	guid: { type: String, default: null },
});

const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);
const userOrganisationId = iframeDataBridge.getCurrentUser()?.organisationId;

const DETAIL_ORDER_TYPE = { ORDERS: 'orders', RETURNS: 'returns' };
const DEFAULT_PERIOD_PRESET = 'last-6-months';

const builderRef = ref(null);
const gridRef = ref(null);
const filterControls = ref(null);

const isLoading = ref(false);
const isViewReady = ref(false);
const viewStateVersion = ref(0);
const isFilterSheetOpen = ref(false);
const isVolumeDialogOpen = ref(false);
const activeFilterCount = ref(0);
const searchText = ref('');

const orders = ref([]);
const coverage = ref({ truncated: false, exceedsOfflineWindow: false, count: 0, source: 'none' });
const deliveryLocations = ref([]);
const organisations = ref([]);
const accounts = ref([]);
const channels = ref([]);
const productTrees = ref([]);
const productById = ref(new Map());
const objectives = ref({ available: false, period: null, value: { ordered: {}, delivered: {} }, weight: { ordered: {}, delivered: {} } });
const savedPeriodSelection = ref(null);

const focusMetrics = ref([METRIC.VALUE, METRIC.WEIGHT]);
const categoryLevel = ref(1);
const selectedRowGroups = ref([]);
const detailOrderTypes = ref([DETAIL_ORDER_TYPE.ORDERS, DETAIL_ORDER_TYPE.RETURNS]);
const expandedBlocks = reactive({ cumul: true, value: true, weight: true });

const isLaptop = ref(deviceUtils.isLaptop());
const isTouchMode = computed(() => deviceUtils.isTouchMode?.() ?? isLaptop.value);
const onResize = () => {
	isLaptop.value = deviceUtils.isLaptop();
};

const appliedFilters = reactive({ period: null, organisationIds: [], accountIds: [], channelIds: [] });

// --- Metric availability -----------------------------------------------------------------------

const objectivesForDisplay = computed(() => {
	if (isPeriodWithinObjective(appliedPeriod.value, objectives.value.period)) return objectives.value;

	const suppress = (base) => ({ ...base, hasObjective: false });
	return {
		...objectives.value,
		value: { ordered: suppress(objectives.value.value.ordered), delivered: suppress(objectives.value.value.delivered) },
		weight: { ordered: suppress(objectives.value.weight.ordered), delivered: suppress(objectives.value.weight.delivered) },
	};
});

const metricOptions = computed(() => [
	{ field: METRIC.VALUE, label: t('Turnover') },
	{ field: METRIC.WEIGHT, label: t('Weight') },
]);

const showValue = computed(() => focusMetrics.value.includes(METRIC.VALUE));
const showWeight = computed(() => focusMetrics.value.includes(METRIC.WEIGHT));

const primaryMetric = computed(() => (showValue.value ? METRIC.VALUE : METRIC.WEIGHT));

// --- Period ------------------------------------------------------------------------------------

const presetContext = computed(() => ({ objectivePeriod: objectives.value.period }));

const defaultPeriod = computed(() => {
	const saved = resolvePeriodSelection(savedPeriodSelection.value, presetContext.value);
	if (saved) return saved;
	const range = presetRange(DEFAULT_PERIOD_PRESET);
	return { startDate: range[0], endDate: range[1] };
});

const appliedPeriod = computed(() => appliedFilters.period ?? defaultPeriod.value);

const periodPresets = computed(() => buildPresets(ORDER_TRACKING_PRESET_KEYS, t, presetContext.value));

const periodLabel = computed(() => formatPeriodLabel(appliedPeriod.value.startDate, appliedPeriod.value.endDate));
const periodMonths = computed(() => periodMonthCount(appliedPeriod.value.startDate, appliedPeriod.value.endDate));

// --- Perimeter resolution ----------------------------------------------------------------------

const ownerScope = computed(() => ownerOrganisationScope(organisations.value, userOrganisationId));

const organisationItems = computed(() => (ownerScope.value ? organisations.value.filter((organisation) => ownerScope.value.has(Number(organisation.id))) : organisations.value));

const deliveryLocationById = computed(() => new Map(deliveryLocations.value.map((dl) => [dl.id, dl])));

const filteredDeliveryLocationIds = computed(() => {
	const hasAccountFilter = appliedFilters.accountIds.length > 0;
	const hasChannelFilter = appliedFilters.channelIds.length > 0;
	if (!hasAccountFilter && !hasChannelFilter) return [];

	const accountSet = new Set(appliedFilters.accountIds);
	const channelSet = new Set(appliedFilters.channelIds);

	return deliveryLocations.value.filter((dl) => (!hasAccountFilter || accountSet.has(dl.accountId)) && (!hasChannelFilter || channelSet.has(dl.channelId))).map((dl) => dl.id);
});

const scopedOrders = computed(() =>
	filterOrders(orders.value, {
		startDate: appliedPeriod.value.startDate,
		endDate: appliedPeriod.value.endDate,
		ownerScope: ownerScope.value,
		organisationIds: appliedFilters.organisationIds,
		deliveryLocationIds: filteredDeliveryLocationIds.value,
	})
);

// --- Aggregates --------------------------------------------------------------------------------

const totals = computed(() => aggregatePeriod(scopedOrders.value));
const showReturns = computed(() => totals.value.hasReturns);

const monthlySeries = computed(() => aggregateByMonth(scopedOrders.value, { start: appliedPeriod.value.startDate, end: appliedPeriod.value.endDate }));

const categoryOptions = computed(() => ({ level: categoryLevel.value, productTrees: productTrees.value, productById: productById.value }));
const valueCategories = computed(() => aggregateByCategory(scopedOrders.value, { ...categoryOptions.value, metric: METRIC.VALUE }));
const weightCategories = computed(() => aggregateByCategory(scopedOrders.value, { ...categoryOptions.value, metric: METRIC.WEIGHT }));

const levelOptions = computed(() => {
	const depths = [...new Set(productTrees.value.map((tree) => Number(tree.depth)).filter((depth) => Number.isFinite(depth) && depth > 0))].sort((a, b) => a - b);
	return (depths.length > 0 ? depths : [1]).map((depth) => ({ value: depth, label: `${t('Level')} ${depth}` }));
});

// --- Detail -------------------------------------------------------------------------------------

const accountById = computed(() => new Map(accounts.value.map((account) => [account.id, account])));
const channelById = computed(() => new Map(channels.value.map((channel) => [channel.id, channel])));

const orderTypeOptions = computed(() => [
	{ field: DETAIL_ORDER_TYPE.ORDERS, label: t('Orders') },
	{ field: DETAIL_ORDER_TYPE.RETURNS, label: t('Returns') },
]);

const showDetailOrders = computed(() => !showReturns.value || detailOrderTypes.value.includes(DETAIL_ORDER_TYPE.ORDERS));
const showDetailReturns = computed(() => showReturns.value && detailOrderTypes.value.includes(DETAIL_ORDER_TYPE.RETURNS));

const detailOrders = computed(() => {
	const orderTypes = [...(showDetailOrders.value ? [ORDER_TYPE.REGULAR] : []), ...(showDetailReturns.value ? [ORDER_TYPE.RETURN] : [])];
	return filterOrders(scopedOrders.value, { orderTypes });
});

const detailRows = computed(() => aggregateByDeliveryLocation(detailOrders.value).map((bucket) => toDetailRow(bucket, deliveryLocationById.value.get(bucket.deliveryLocationId), { accountById: accountById.value, channelById: channelById.value })));

const visibleDetailRows = computed(() => {
	const needle = searchText.value?.trim().toLowerCase();
	if (!needle) return detailRows.value;
	return detailRows.value.filter((row) =>
		[row.storeName, row.companyName, row.channelName].some((field) =>
			String(field ?? '')
				.toLowerCase()
				.includes(needle)
		)
	);
});

const columnDefs = computed(() => buildColumnDefs({ t, showValue: showValue.value, showWeight: showWeight.value, showReturns: showDetailReturns.value, showOrders: showDetailOrders.value }));
const mobileColumns = computed(() => buildMobileColumns({ t, showValue: showValue.value, showWeight: showWeight.value, showReturns: showDetailReturns.value, showOrders: showDetailOrders.value }));
const groupRowAgg = buildGroupRowAgg();

const rowGroupDefinitions = computed(() => [
	{ field: 'companyName', label: t('Account') },
	{ field: 'channelName', label: t('Channel') },
]);

const rowGroupFieldByColId = computed(() => Object.fromEntries(rowGroupDefinitions.value.map((group) => [group.field, group.field])));

function haveSameItems(first = [], second = []) {
	if (first.length !== second.length) return false;
	const secondSet = new Set(second);
	return first.every((item) => secondSet.has(item));
}

watch(selectedRowGroups, (groups) => {
	const api = gridRef.value?.getGridApi?.();
	const currentGroups =
		typeof api?.getRowGroupColumns === 'function'
			? api
					.getRowGroupColumns()
					.map((column) => column?.getColId?.())
					.filter(Boolean)
			: [];
	if (haveSameItems(currentGroups, groups)) return;
	gridRef.value?.setRowGroupColumns?.(groups);
});

// --- Coverage warning --------------------------------------------------------------------------

const showTruncationWarning = computed(() => coverage.value.exceedsOfflineWindow);
const truncationMessage = computed(() => t('OfflineHistoryLimited').replace('%months%', MAX_OFFLINE_MONTHS));
const volumeTooLargeMessage = computed(() => t('OrderVolumeTooLarge').replace('%max%', formatNumber(MAX_NETWORK_ORDERS)));

// --- Filters ------------------------------------------------------------------------------------

const filterDefinitions = computed(() => [
	{ key: 'period', type: 'daterange', label: t('Period'), presets: periodPresets.value, required: true, preSelected: [defaultPeriod.value.startDate, defaultPeriod.value.endDate], section: 'perimeter' },
	{ key: 'organisations', label: t('Organisations'), items: organisationItems.value, titleKey: 'label', multiple: true, section: 'perimeter' },
	{ key: 'accounts', label: t('Accounts'), items: accounts.value, titleKey: 'name', multiple: true, section: 'pointOfSale' },
	{ key: 'channels', label: t('Channels'), items: channels.value, titleKey: 'name', multiple: true, section: 'pointOfSale' },
]);

const selectedFilterChips = computed(() =>
	buildFilterChips([
		{ key: 'organisations', label: t('Organisations'), values: filterControls.value?.controls?.organisations?.getFullValues?.(), valueKey: 'label' },
		{ key: 'accounts', label: t('Accounts'), values: filterControls.value?.controls?.accounts?.getFullValues?.(), valueKey: 'name' },
		{ key: 'channels', label: t('Channels'), values: filterControls.value?.controls?.channels?.getFullValues?.(), valueKey: 'name' },
	])
);

function onFilterChipClick(chip) {
	isFilterSheetOpen.value = true;
	scrollFilterFieldIntoView(chip.key, builderRef.value);
}

function removeFilterChip(chip) {
	filterControls.value?.controls?.[chip.key]?.reset?.();
	onFiltersApply();
}

async function onFiltersApply() {
	const controls = filterControls.value?.controls ?? {};
	const period = controls.period?.getFullValues?.();
	const previousPeriod = appliedFilters.period;

	appliedFilters.period = period ? { startDate: period.startDate, endDate: period.endDate } : null;
	appliedFilters.organisationIds = controls.organisations?.getValues?.() ?? [];
	appliedFilters.accountIds = controls.accounts?.getValues?.() ?? [];
	appliedFilters.channelIds = controls.channels?.getValues?.() ?? [];

	isFilterSheetOpen.value = false;
	if (await loadOrdersWithinCeiling(() => (appliedFilters.period = previousPeriod))) persistPeriod();
}

function resetFilters() {
	if (savedPeriodSelection.value) {
		savedPeriodSelection.value = null;
		savePeriodSelection(null).catch((error) => log('warn', 'OrderTracking', `⚠️ Saved period not cleared: ${error.message}`));
	}
	Object.values(filterControls.value?.controls ?? {}).forEach((control) => control?.reset?.());
	appliedFilters.period = null;
	appliedFilters.organisationIds = [];
	appliedFilters.accountIds = [];
	appliedFilters.channelIds = [];
	loadOrders();
}

function persistPeriod() {
	const { startDate, endDate } = appliedPeriod.value;
	const selection = serializePeriodSelection(startDate, endDate, ORDER_TRACKING_PRESET_KEYS, presetContext.value);
	const current = savedPeriodSelection.value ?? { preset: DEFAULT_PERIOD_PRESET };
	if (JSON.stringify(selection) === JSON.stringify(current)) return;

	savedPeriodSelection.value = selection;
	savePeriodSelection(selection).catch((error) => log('warn', 'OrderTracking', `⚠️ Period not saved: ${error.message}`));
}

function bumpViewStateVersion() {
	viewStateVersion.value += 1;
}

// --- User views ---------------------------------------------------------------------------------

function getUserViewState() {
	return {
		period: appliedFilters.period ? { startDate: new Date(appliedFilters.period.startDate).toISOString(), endDate: new Date(appliedFilters.period.endDate).toISOString() } : null,
		organisationIds: [...appliedFilters.organisationIds],
		accountIds: [...appliedFilters.accountIds],
		channelIds: [...appliedFilters.channelIds],
		focusMetrics: [...focusMetrics.value],
		categoryLevel: categoryLevel.value,
		detailOrderTypes: [...detailOrderTypes.value],
		expandedBlocks: { ...expandedBlocks },
		grid: gridRef.value?.getViewState?.() ?? null,
	};
}

function getDefaultUserViewState() {
	return { period: null, organisationIds: [], accountIds: [], channelIds: [], focusMetrics: [METRIC.VALUE, METRIC.WEIGHT], categoryLevel: 1, detailOrderTypes: [DETAIL_ORDER_TYPE.ORDERS, DETAIL_ORDER_TYPE.RETURNS], expandedBlocks: { cumul: true, value: true, weight: true }, grid: null };
}

async function applyUserViewState(state) {
	if (!state) return;

	appliedFilters.period = state.period ? { startDate: new Date(state.period.startDate), endDate: new Date(state.period.endDate) } : null;
	appliedFilters.organisationIds = state.organisationIds ?? [];
	appliedFilters.accountIds = state.accountIds ?? [];
	appliedFilters.channelIds = state.channelIds ?? [];
	focusMetrics.value = state.focusMetrics?.length ? [...state.focusMetrics] : [METRIC.VALUE, METRIC.WEIGHT];
	categoryLevel.value = state.categoryLevel ?? 1;
	detailOrderTypes.value = state.detailOrderTypes?.length ? [...state.detailOrderTypes] : [DETAIL_ORDER_TYPE.ORDERS, DETAIL_ORDER_TYPE.RETURNS];
	Object.assign(expandedBlocks, state.expandedBlocks ?? {});

	const controls = filterControls.value?.controls ?? {};
	controls.period?.selectItems?.([appliedPeriod.value.startDate, appliedPeriod.value.endDate]);
	controls.organisations?.selectItems?.(appliedFilters.organisationIds);
	controls.accounts?.selectItems?.(appliedFilters.accountIds);
	controls.channels?.selectItems?.(appliedFilters.channelIds);

	await loadOrdersWithinCeiling(() => (appliedFilters.period = null));
	if (state.grid) gridRef.value?.applyViewState?.(state.grid);
}

// --- Loading -------------------------------------------------------------------------------------

async function loadReferenceData() {
	const [dls, orgs, accountList, channelList, trees, assortProducts, resolvedObjectives, periodSelection] = await Promise.all([getDeliveryLocations().catch(() => []), getOrganisations().catch(() => []), getAccounts().catch(() => []), getChannels().catch(() => []), getProductTrees().catch(() => []), getAssortProducts().catch(() => []), resolveObjectives(), loadPeriodSelection()]);

	savedPeriodSelection.value = periodSelection;
	deliveryLocations.value = Array.isArray(dls) ? dls : [];
	organisations.value = Array.isArray(orgs) ? orgs : [];
	accounts.value = Array.isArray(accountList) ? accountList : [];
	channels.value = Array.isArray(channelList) ? channelList : [];
	productTrees.value = Array.isArray(trees) ? trees : [];
	productById.value = new Map((Array.isArray(assortProducts) ? assortProducts : []).map((product) => [product.id, product]));
	objectives.value = resolvedObjectives;
}

async function loadOrders() {
	isLoading.value = true;
	try {
		const result = await getDirectOrders({ startDate: appliedPeriod.value.startDate, endDate: appliedPeriod.value.endDate });
		if (result.coverage.tooLarge) return false;
		orders.value = result.orders;
		coverage.value = result.coverage;
		log('info', 'OrderTracking', `📊 ${result.orders.length} orders (${result.coverage.source}${result.coverage.truncated ? ', truncated' : ''})`);
	} catch (error) {
		log('error', 'OrderTracking', `❌ Order load failed: ${error.message}`);
		orders.value = [];
	} finally {
		isLoading.value = false;
	}
	return true;
}

async function loadOrdersWithinCeiling(revert) {
	if (await loadOrders()) return true;

	revert();
	filterControls.value?.controls?.period?.selectItems?.([appliedPeriod.value.startDate, appliedPeriod.value.endDate]);
	isVolumeDialogOpen.value = true;
	await loadOrders();
	return false;
}

onMounted(async () => {
	window.addEventListener('resize', onResize);

	await loadReferenceData();
	await loadOrdersWithinCeiling(() => (savedPeriodSelection.value = null));
	isViewReady.value = true;
	bumpViewStateVersion();
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', onResize);
});
</script>

<style scoped lang="scss">
@use '@/styles/variables' as *;

.order-tracking-builder {
	&__context {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px 24px;
	}

	&__focus {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	&__focus-label {
		font-size: $font-size-small;
		color: rgba(var(--v-theme-on-surface), 0.68);
	}

	&__rates {
		display: flex;
		flex-wrap: nowrap;
		overflow-x: auto;
		gap: 8px;
		margin-bottom: 16px;
	}

	&__cumul {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
		gap: 16px;
	}
}
</style>
